import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Select, InputNumber, Spin, message, Typography, Space } from 'antd';
import { accountApi } from '../services/api';

const { Search } = Input;

const fmt = (v) => (v == null ? '-' : Number(v).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export default function AssetRankPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [positionsMap, setPositionsMap] = useState({});  // client_id -> positions[]
  const [loadingPos, setLoadingPos] = useState(false);
  const [posFilter, setPosFilter] = useState('all');   // all / has / none
  const [minAsset, setMinAsset] = useState(null);      // 总资产大于
  const [minPosValue, setMinPosValue] = useState(null); // 持仓金额大于

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const res = await accountApi.getAll();
      if (res.success) {
        const list = res.data || [];
        list.sort((a, b) => (b.total_asset || 0) - (a.total_asset || 0)); // 资金排行：总资产降序
        setAccounts(list);
      } else {
        message.error(res.message || '加载失败');
      }
    } catch {
      message.error('加载资金账户失败');
    } finally {
      setLoading(false);
    }
  };

  // 一次性拉取所有持仓，按 client_id 分组（避免逐客户请求）
  const loadAllPositions = async () => {
    setLoadingPos(true);
    try {
      const res = await accountApi.getAllPositions();
      if (res.success) {
        const map = {};
        (res.data || []).forEach((p) => {
          if (!p.client_id) return;
          (map[p.client_id] = map[p.client_id] || []).push(p);
        });
        setPositionsMap(map);
      }
    } catch {
      // 忽略持仓加载失败，不影响账户列表展示
    } finally {
      setLoadingPos(false);
    }
  };

  useEffect(() => { loadAccounts(); loadAllPositions(); }, []);

  // 计算某客户持仓市值合计
  const posValueOf = (clientId) => {
    const list = positionsMap[clientId] || [];
    return list.reduce((s, p) => s + (Number(p.market_value) || 0), 0);
  };

  // 关键字 + 持仓 + 资金 + 持仓金额 综合过滤
  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return accounts.filter((a) => {
      if (kw) {
        const hit = (a.client_id || '').toLowerCase().includes(kw) ||
          (a.client_name || '').toLowerCase().includes(kw) ||
          (a.name || '').toLowerCase().includes(kw);
        if (!hit) return false;
      }
      const hasPos = (positionsMap[a.client_id] || []).length > 0;
      if (posFilter === 'has' && !hasPos) return false;
      if (posFilter === 'none' && hasPos) return false;
      if (minAsset != null && (a.total_asset || 0) <= minAsset) return false;
      if (minPosValue != null && posValueOf(a.client_id) <= minPosValue) return false;
      return true;
    });
  }, [accounts, keyword, posFilter, minAsset, minPosValue, positionsMap]);

  const resetFilters = () => {
    setKeyword(''); setPosFilter('all'); setMinAsset(null); setMinPosValue(null); setPage(1);
  };

  const columns = [
    { title: '#', key: 'rank', width: 60, render: (_, __, idx) => (page - 1) * pageSize + idx + 1 },
    { title: '客户', dataIndex: 'client_id', key: 'client_id', width: 170,
      render: (v, r) => (
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</div>
          <a
            style={{ fontSize: 12 }}
            onClick={() => navigate(`/accounts?client_id=${encodeURIComponent(r.client_id)}`)}
          >
            {r.client_name || '-'}
          </a>
        </div>
      ) },
    { title: '总资产', dataIndex: 'total_asset', key: 'total_asset', width: 130, align: 'right',
      render: (v) => <span style={{ color: '#cf1322', fontWeight: 600 }}>{fmt(v)}</span> },
    { title: '可用资金', dataIndex: 'available', key: 'available', width: 130, align: 'right',
      render: (v) => <span style={{ color: '#389e0d' }}>{fmt(v)}</span> },
    { title: '持仓金额', dataIndex: 'market_value', key: 'market_value', width: 130, align: 'right',
      render: (v) => <span style={{ color: '#1677ff' }}>{fmt(v)}</span> },
    { title: '持仓明细', key: 'positions', width: 320,
      render: (_, r) => {
        if (loadingPos) return <Spin size="small" />;
        const list = positionsMap[r.client_id] || [];
        if (list.length === 0) return <span style={{ color: '#999' }}>无持仓</span>;
        return (
          <div style={{ fontSize: 12, lineHeight: '20px' }}>
            {list.map((p) => (
              <div key={p.id || p.stock_code} style={{ whiteSpace: 'nowrap' }}>
                {p.stock_name} <span style={{ color: '#999', fontFamily: 'monospace' }}>({p.stock_code})</span>
                {' '}数量{p.quantity != null ? p.quantity.toLocaleString() : '-'}
                {' '}金额{fmt(p.market_value)}
              </div>
            ))}
          </div>
        );
      } },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>资金排行</h2>
        <Search
          placeholder="搜索客户ID或名称"
          allowClear
          style={{ width: 220 }}
          onChange={(e) => { setKeyword(e.target.value); setPage(1); }}
        />
      </div>
      <Space wrap style={{ marginBottom: 12 }} size="middle">
        <span>
          <span style={{ marginRight: 6, color: '#555' }}>持仓</span>
          <Select
            value={posFilter}
            style={{ width: 110 }}
            onChange={(v) => { setPosFilter(v); setPage(1); }}
            options={[
              { value: 'all', label: '全部' },
              { value: 'has', label: '有持仓' },
              { value: 'none', label: '无持仓' },
            ]}
          />
        </span>
        <span>
          <span style={{ marginRight: 6, color: '#555' }}>资金大于</span>
          <InputNumber
            placeholder="金额"
            style={{ width: 150 }}
            min={0}
            step={10000}
            value={minAsset}
            onChange={(v) => { setMinAsset(v); setPage(1); }}
            formatter={(v) => `¥ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(v) => Number((v || '').replace(/[^\d.]/g, ''))}
          />
        </span>
        <span>
          <span style={{ marginRight: 6, color: '#555' }}>持仓金额大于</span>
          <InputNumber
            placeholder="金额"
            style={{ width: 160 }}
            min={0}
            step={10000}
            value={minPosValue}
            onChange={(v) => { setMinPosValue(v); setPage(1); }}
            formatter={(v) => `¥ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(v) => Number((v || '').replace(/[^\d.]/g, ''))}
          />
        </span>
        <a onClick={resetFilters}>重置筛选</a>
      </Space>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filtered}
        pagination={{ current: page, pageSize, total: filtered.length, onChange: setPage, showTotal: (t) => `共 ${t} 条` }}
        size="middle"
      />
      <Typography.Paragraph type="secondary" style={{ marginTop: 12, fontSize: 12 }}>
        按总资产降序排列；持仓明细直接在表格内逐行展示（股票名称/代码、持仓数量、持仓金额）。
      </Typography.Paragraph>
    </div>
  );
}
